import React, { useContext } from "react";
import { useHistory } from "react-router-dom";
import JoinGame from "./JoinGame";
import CardStack from "./CardStack";
import WaitingRoom from "./WaitingRoom";
import Judging from "./Judging";
import Winner from "./Winner";
import Loading from "./Loading";
import ScoreBox from "./ScoreBox";
import { GlobalContext } from "../context/GlobalState";
import { sameId, parseCards, hasPlayed, cardMarkup } from "./gameHelpers";

const Waiting = ({ message, blackCard }) => (
  <div className="tm2">
    {blackCard && (
      <div
        className="blackcard--preview cardstack__black"
        dangerouslySetInnerHTML={cardMarkup(blackCard)}
      />
    )}
    <h3>
      <i className="fas fa-circle-notch fa-spin"></i> {message}
    </h3>
  </div>
);

const GamePlay = ({ match }) => {
  const { game, players, currentPlayerId, resetGame } = useContext(GlobalContext);
  const history = useHistory();

  const urlCode = match.params.gamecode;

  if (game?.loading) {
    return <Loading />;
  }

  // No session yet (cold visit / shared link before joining): show the join form.
  // Also if the link points at a different game than the one in progress.
  if (!game?.code || (urlCode && game.code !== urlCode)) {
    return <JoinGame prefillCode={urlCode} />;
  }

  // Session restored from a refresh, but the game state hasn't been fetched yet.
  if (game.status === undefined && !game.winner) {
    return <Loading />;
  }

  const handlePlayAgain = () => {
    resetGame();
    history.push("/");
  };

  const me = players.find((p) => sameId(p.id, currentPlayerId));

  // Removed from the game by the host (identity no longer in the player list).
  if (currentPlayerId != null && players.length > 0 && !me) {
    return (
      <div className="tm2">
        <div className="box">
          <h3 style={{ margin: 0 }}>You've been removed from this game.</h3>
        </div>
        <button className="btn btn-gold" onClick={handlePlayAgain}>
          Back to Home
        </button>
      </div>
    );
  }

  // A set winner ends the game (there is no "finished" status on the backend).
  if (game.winner) {
    const winner = players.find((p) => sameId(p.id, game.winner));
    return (
      <Winner
        winnerName={winner ? winner.name : "Someone"}
        onPlayAgain={handlePlayAgain}
      />
    );
  }

  // status 0 = lobby, status 1 = in progress.
  if (Number(game.status) === 0) {
    return <WaitingRoom />;
  }

  const isJudge = sameId(game.current_judge, currentPlayerId);
  const blackCard = game.current_black_card;

  // Everyone except the judge plays a card each round.
  const contestants = players.filter((p) => !sameId(p.id, game.current_judge));
  const submissions = contestants
    .filter(hasPlayed)
    .map((p) => ({ playerId: p.id, name: p.name, card: p.current_card_1 }));
  const allPlayed =
    contestants.length > 0 && submissions.length === contestants.length;

  let phase;
  if (isJudge) {
    phase = (
      <Judging
        blackCard={blackCard}
        allPlayed={allPlayed}
        submissions={submissions}
        waitingOn={contestants.length - submissions.length}
      />
    );
  } else if (!hasPlayed(me)) {
    phase = <CardStack blackCard={blackCard} hand={parseCards(me?.cards)} />;
  } else {
    phase = (
      <Waiting
        blackCard={blackCard}
        message="Card played. Waiting for the round to finish..."
      />
    );
  }

  return (
    <React.Fragment>
      <ScoreBox players={players} judgeId={game.current_judge} />
      {phase}
    </React.Fragment>
  );
};

export default GamePlay;
